import { useCallback, useEffect, useRef, useState } from 'react';
import { listJoinRequests } from '../lib/api/joinRequests';
import type { View } from '../app/views/types';

// ═══════════════════════════════════════════════════════════════════
// WHAT IS WAITING FOR THE PEOPLE WHO RUN THIS ORGANIZATION (owner, 2026-10-20).
//
// «Når man er innlogget som senteradmin trenger vi en notifikasjonsknapp, den
// må vise når det er actions som kreves, f.eks. når et medlem søker om å bli
// godkjent.» The header bell reads this hook; this hook asks the server.
//
// ── A LIST OF KINDS, NOT ONE NUMBER ───────────────────────────────
//
// Today exactly one thing can wait: a membership request nobody has answered.
// But the bell is «what needs me», not «how many applications», so the shape
// is a list of ACTION KINDS — each with its count and the view where the
// person deals with it — and the badge is their sum. A second kind (bookings
// to confirm, a payout to approve) is one more entry in `SOURCES`, and the
// bell, the menu and the badge need no change.
//
// ── THE SERVER DECIDES WHO SEES IT ────────────────────────────────
//
// The bell draws for whoever may read the queue, and «may» is answered by
// the endpoint, never by a role key in the browser: a 401/403 from a source
// means «not yours», and that source is dropped. With every source refused
// the bell is absent entirely — not disabled, not zero. The shell also does
// not ask at all outside an organization context, because there is no queue
// to be «yours» on the personal home.
//
// ── NEVER HAMMER THE API ──────────────────────────────────────────
//
// One request per source, on: entering an organization, a modest interval
// (a minute), the tab becoming visible again, and navigation — the last two
// THROTTLED, so flicking between views re-reads at most once per
// `minGapMs`. A hidden tab skips its ticks. No list endpoint is being polled
// every few seconds for a number that changes a few times a week.
//
// The list endpoint is used as it is rather than a count endpoint: the queue
// is short by construction (a standing cap of ten pending per player and a
// club that answers), so the rows it returns are the count, and a second
// endpoint would be a second thing to guard and to keep in step.
// ═══════════════════════════════════════════════════════════════════

export type PendingActionKind = 'join_requests';

export interface PendingAction {
  kind: PendingActionKind;
  /** How many of this kind wait. Zero is a real answer: «you may see this, and nothing waits». */
  count: number;
  /** Where the person deals with it. Navigating there IS the action. */
  view: View;
}

/** What one source reports: a count, «not yours», or «could not find out». */
export type SourceAnswer = { count: number } | 'refused' | 'unknown';

export interface PendingActionSource {
  kind: PendingActionKind;
  view: View;
  load: () => Promise<SourceAnswer>;
}

/**
 * The join queue, through the SAME client the queue panel uses — so the bell
 * cannot disagree with the panel about what the server said.
 */
async function loadJoinRequests(): Promise<SourceAnswer> {
  const r = await listJoinRequests('pending');
  if (r.status === 'ok') return { count: r.requests.length };
  if (r.status === 'refused') return 'refused';
  return 'unknown';
}

export const PENDING_ACTION_SOURCES: readonly PendingActionSource[] = [
  { kind: 'join_requests', view: 'players', load: loadJoinRequests },
];

export const PENDING_ACTIONS_POLL_MS = 60_000;
/** Navigation and focus re-read at most this often. */
export const PENDING_ACTIONS_MIN_GAP_MS = 15_000;

export interface UsePendingActionsOptions {
  /** Ask at all. False outside an organization context, or off the API backend. */
  enabled: boolean;
  /** The organization being worked in; a change re-reads from scratch. */
  tenantId: string | null;
  /**
   * Anything whose change should prompt a (throttled) re-read — the shell
   * passes the current view, so coming back from the queue refreshes the badge.
   */
  navigationKey?: unknown;
  pollMs?: number;
  minGapMs?: number;
  sources?: readonly PendingActionSource[];
  now?: () => number;
}

export interface PendingActionsApi {
  /** The kinds this person may see, each with its count. Empty until answered, and empty when none is theirs. */
  actions: PendingAction[];
  /** The badge: every count summed. */
  total: number;
  /** Ask again now, ignoring the throttle. */
  refresh: () => void;
}

export function usePendingActions({
  enabled,
  tenantId,
  navigationKey,
  pollMs = PENDING_ACTIONS_POLL_MS,
  minGapMs = PENDING_ACTIONS_MIN_GAP_MS,
  sources = PENDING_ACTION_SOURCES,
  now = Date.now,
}: UsePendingActionsOptions): PendingActionsApi {
  const [actions, setActions] = useState<PendingAction[]>([]);
  const lastReadAt = useRef<number | null>(null);
  // Which (tenant, enabled) generation an answer belongs to. An answer for the
  // organization somebody just LEFT must not land on the one they entered.
  const generation = useRef(0);
  // Read through refs so a caller passing a fresh function each render does
  // not make `read` a new function — which would make the «entered an
  // organization» effect below fire on every render, and THAT is hammering.
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;
  const nowRef = useRef(now);
  nowRef.current = now;

  const read = useCallback(async () => {
    const mine = generation.current;
    lastReadAt.current = nowRef.current();
    const answers = await Promise.all(
      sourcesRef.current.map(async (s) => {
        let answer: SourceAnswer;
        try {
          answer = await s.load();
        } catch {
          answer = 'unknown';
        }
        return { source: s, answer };
      }),
    );
    if (mine !== generation.current) return;
    setActions((previous) => {
      const next: PendingAction[] = [];
      for (const { source, answer } of answers) {
        if (answer === 'refused') continue;
        if (answer === 'unknown') {
          // Could not find out: keep what we last knew rather than making
          // the bell blink out on a network blip. Nothing known → nothing.
          const known = previous.find((a) => a.kind === source.kind);
          if (known) next.push(known);
          continue;
        }
        next.push({ kind: source.kind, count: answer.count, view: source.view });
      }
      return next;
    });
  }, []);

  // Entering (or leaving) an organization: forget, then ask.
  useEffect(() => {
    generation.current += 1;
    lastReadAt.current = null;
    setActions([]);
    if (!enabled || tenantId === null) return undefined;
    void read();
    return undefined;
  }, [enabled, tenantId, read]);

  // The interval, skipping ticks nobody would see.
  useEffect(() => {
    if (!enabled || tenantId === null) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void read();
    }, pollMs);
    return () => clearInterval(id);
  }, [enabled, tenantId, pollMs, read]);

  // Throttled re-reads: a view change, and the tab becoming visible again.
  const throttled = useCallback(() => {
    if (!enabled || tenantId === null) return;
    const last = lastReadAt.current;
    if (last !== null && nowRef.current() - last < minGapMs) return;
    void read();
  }, [enabled, tenantId, minGapMs, read]);

  const firstNavigation = useRef(true);
  useEffect(() => {
    // The mount itself is covered by the effect above; only a CHANGE counts.
    if (firstNavigation.current) {
      firstNavigation.current = false;
      return;
    }
    throttled();
    // `navigationKey` is the trigger; `throttled` already carries the inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationKey]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisible = () => {
      if (!document.hidden) throttled();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [throttled]);

  const refresh = useCallback(() => {
    if (!enabled || tenantId === null) return;
    void read();
  }, [enabled, tenantId, read]);

  const total = actions.reduce((sum, a) => sum + a.count, 0);
  return { actions, total, refresh };
}
